"use client";

import { c as _c } from "react/compiler-runtime";
import { TRPCClientError } from "@trpc/client";
import { useSession } from "next-auth/react";
import { useEffect } from "react";
import { usePlaygroundInitialData } from "#/features/playground/context/PlaygroundInitialDataContext";
import { projectSlice, selectIsEditable } from "#/features/project/model/projectSlice";
import { usePlaygroundSlugs } from "#/shared/hooks";
import { usePlaygroundRoute } from "#/shared/hooks/usePlaygroundRoute";
import { api } from "#/shared/lib";
import { useAppDispatch, useAppSelector } from "#/store/hooks";

/**
 * Encapsulates the project/case data fetching and side effects
 * shared between `ProjectPanel` (desktop) and `MobileCodeView` (mobile).
 */
export const useProjectPanelData = () => {
  const $ = _c(39);
  const session = useSession();
  const dispatch = useAppDispatch();
  const {
    projectSlug: t0,
    caseSlug: t1,
    clearSlugs
  } = usePlaygroundSlugs();
  const projectSlug = t0 === undefined ? "" : t0;
  const caseSlug = t1 === undefined ? "" : t1;
  const route = usePlaygroundRoute();
  const serverInitialData = usePlaygroundInitialData();
  const isEditable = useAppSelector(selectIsEditable);
  const t2 = Boolean(projectSlug);
  const t3 = serverInitialData?.projectBySlug?.slug === projectSlug ? serverInitialData.projectBySlug : undefined;
  let t4;
  if ($[0] !== t2 || $[1] !== t3) {
    t4 = {
      enabled: t2,
      initialData: t3,
      retry(failureCount, error) {
        if (error instanceof TRPCClientError && error.data.code === "NOT_FOUND") {
          return false;
        }
        return failureCount < 4;
      }
    };
    $[0] = t2;
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const selectedProject = api.project.getBySlug.useQuery(projectSlug, t4);
  let t5;
  let t6;
  if ($[3] !== dispatch || $[4] !== selectedProject.data) {
    t5 = () => {
      if (selectedProject.data) {
        dispatch(projectSlice.actions.changeProjectId(selectedProject.data.id));
      }
    };
    t6 = [selectedProject.data, dispatch];
    $[3] = dispatch;
    $[4] = selectedProject.data;
    $[5] = t5;
    $[6] = t6;
  } else {
    t5 = $[5];
    t6 = $[6];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[7] !== dispatch || $[8] !== selectedProject.error) {
    t7 = () => {
      if (selectedProject.error) {
        dispatch(projectSlice.actions.loadFinish());
      }
    };
    t8 = [selectedProject.error, dispatch];
    $[7] = dispatch;
    $[8] = selectedProject.error;
    $[9] = t7;
    $[10] = t8;
  } else {
    t7 = $[9];
    t8 = $[10];
  }
  useEffect(t7, t8);
  const t9 = selectedProject.data?.id || "";
  let t10;
  if ($[11] !== caseSlug || $[12] !== t9) {
    t10 = {
      projectId: t9,
      slug: caseSlug
    };
    $[11] = caseSlug;
    $[12] = t9;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const t11 = Boolean(selectedProject.data?.id && caseSlug);
  const t12 = serverInitialData?.caseBySlug?.slug === caseSlug && serverInitialData.projectBySlug?.id === selectedProject.data?.id ? serverInitialData.caseBySlug : undefined;
  let t13;
  if ($[14] !== t11 || $[15] !== t12) {
    t13 = {
      enabled: t11,
      initialData: t12
    };
    $[14] = t11;
    $[15] = t12;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  const selectedCase = api.project.getCaseBySlug.useQuery(t10, t13);
  let t14;
  let t15;
  if ($[17] !== clearSlugs || $[18] !== dispatch || $[19] !== isEditable || $[20] !== route || $[21] !== selectedProject.data || $[22] !== selectedProject.error || $[23] !== session.data) {
    t14 = () => {
      if (selectedProject.error) {
        console.error("selectedProject.error: ", selectedProject.error);
        if (route) {
          route.navigateTo(route.basePath, {
            omitView: true
          });
        } else {
          clearSlugs();
        }
        return;
      }
      if (!selectedProject.data || !session.data) {
        if (isEditable) {
          dispatch(projectSlice.actions.changeIsEditable(false));
        }
        return;
      }
      const user = session.data.user;
      const newState = user.isAdmin || selectedProject.data.userId === user.id;
      if (isEditable !== newState) {
        dispatch(projectSlice.actions.changeIsEditable(newState));
      }
    };
    t15 = [clearSlugs, dispatch, isEditable, route, selectedProject.data, selectedProject.error, session.data];
    $[17] = clearSlugs;
    $[18] = dispatch;
    $[19] = isEditable;
    $[20] = route;
    $[21] = selectedProject.data;
    $[22] = selectedProject.error;
    $[23] = session.data;
    $[24] = t14;
    $[25] = t15;
  } else {
    t14 = $[24];
    t15 = $[25];
  }
  useEffect(t14, t15);
  let t16;
  let t17;
  if ($[26] !== caseSlug || $[27] !== dispatch || $[28] !== selectedProject.data || $[29] !== selectedProject.isLoading) {
    t16 = () => {
      if (!selectedProject.data || selectedProject.isLoading) {
        return;
      }
      const {
        cases,
        solutions
      } = selectedProject.data;
      if (cases.length === 0) {
        dispatch(projectSlice.actions.loadFinish());
        return;
      }
      if (!caseSlug) {
        return;
      }
      if (solutions.length === 0) {
        dispatch(projectSlice.actions.loadFinish());
      }
    };
    t17 = [caseSlug, dispatch, selectedProject.data, selectedProject.isLoading];
    $[26] = caseSlug;
    $[27] = dispatch;
    $[28] = selectedProject.data;
    $[29] = selectedProject.isLoading;
    $[30] = t16;
    $[31] = t17;
  } else {
    t16 = $[30];
    t17 = $[31];
  }
  useEffect(t16, t17);
  let t18;
  if ($[32] !== caseSlug || $[33] !== isEditable || $[34] !== projectSlug || $[35] !== selectedCase || $[36] !== selectedProject || $[37] !== session) {
    t18 = {
      session,
      isEditable,
      selectedProject,
      selectedCase,
      projectSlug,
      caseSlug
    };
    $[32] = caseSlug;
    $[33] = isEditable;
    $[34] = projectSlug;
    $[35] = selectedCase;
    $[36] = selectedProject;
    $[37] = session;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  return t18 as const;
};